import { createContext, type VNode } from 'preact';
import type { Locale } from '@frame/protocol';

export type Mode = 'idle' | 'active';

/** A playback returned by rt.playAudio. */
export interface AudioPlayback {
  /** Resolves when the sound ends or is stopped with stop(); rejects if downloading or decoding fails. */
  done: Promise<void>;
  stop(): void;
}

/** What the TV runtime offers widgets (implemented by the runtime). Most widgets use the hooks instead. */
export interface RuntimeApi {
  /** True on the real TV; false in a browser and in the panel preview. */
  isTV: boolean;
  /** True in the panel preview iframe. */
  preview: boolean;
  /** Server base URL, ending in '/'. */
  baseUrl: string;
  action(widgetId: string, name: string, input?: unknown): Promise<unknown>;
  getChannel(module: string, channel: string): unknown;
  subscribe(module: string, channel: string, cb: (value: unknown) => void): () => void;
  mode(): Mode;
  onMode(cb: (mode: Mode) => void): () => void;
  /** Language of the TV and all apps on it (set in the panel). */
  locale(): Locale;
  onLocale(cb: (locale: Locale) => void): () => void;
  sleep(): void;
  wake(): void;
  /**
   * Call a TV API by path: 'tizen.tvaudiocontrol.getVolume' (the 'tizen.'/'webapis.' prefix is optional).
   * Callback-based APIs resolve as Promises; results are plain JSON objects. In the preview (isTV=false)
   * calls reject, except 'tizen.application.launch', which shows a toast.
   */
  tv(api: string, ...args: unknown[]): Promise<unknown>;
  onCommand(name: string, cb: (data?: unknown) => void): () => void;
  /** Render a zone's widgets (e.g. the Settings app renders the settings zone). */
  renderZone(name: string): VNode | null;
  /** Keep the TV out of gallery mode while a full-screen UI is open; call the returned function to release. */
  holdAwake(): () => void;
  /** Intercept the Back key (to close an open layer). The most recently added handler runs first; returning true consumes the key. */
  onBack(cb: () => boolean): () => void;
  /** Bring the launcher back to the front (e.g. after opening another app). No-op outside the TV. */
  bringToFront(): void;
  /**
   * Open a TV app; when the user leaves it with Back/Home, the launcher comes back to the front.
   * Also callable from the server: ctx.tv.call('frame.openApp', appId, opts).
   */
  openApp(appId: string, opts?: OpenAppOptions): Promise<void>;
  /** Show a short message at the bottom of the screen for a few seconds. */
  toast(message: string): void;
  /**
   * Play a sound (mp3/wav…; via Web Audio). On the TV, <audio>, <video> and webapis.avplay fire their
   * events but produce no sound from the speakers; don't use them (docs/tizen_permissions_guide.md 2.2.1).
   */
  playAudio(url: string, opts?: { volume?: number }): AudioPlayback;
  /** Download and decode a sound ahead of time so the next playAudio(url) starts without delay. */
  preloadAudio(url: string): void;
  /** Write to the server log (panel → Logs); source defaults to 'runtime'. */
  log(level: 'info' | 'warn' | 'error', message: string, source?: string): void;
  /** URL of a file in a module's assets/ folder (content-hashed when the module has a bundle). */
  assetUrl(module: string, path: string): string;
  /** State shared on the TV between widgets of the same module (not persisted, not sent to the server). */
  getShared(module: string, key: string): unknown;
  setShared(module: string, key: string, value: unknown): void;
  onShared(module: string, key: string, cb: (value: unknown) => void): () => void;
}

export interface OpenAppOptions {
  /** Store ID used to watch for the return to the launcher (automatic for well-known apps). */
  storeId?: string;
  /** Deep link: a tizen.ApplicationControl or a plain object { operation, uri?, mime?, data?: [{ key, value: string[] }] }. */
  control?: unknown;
  /** Launch via DIAL: POST http://127.0.0.1:8080/ws/apps/<name> with `body` as the request body (e.g. YouTube, "v=<id>"). */
  dial?: { name: string; body?: string };
}

export interface WidgetInfo {
  /** Persistent instance id ('w_…'). */
  id: string;
  module: string;
  /** Widget name within the module (the part after '/'). */
  type: string;
  /** The instance's settings, parsed with the widget's zod schema. */
  settings: Record<string, any>;
  /** Size on the launcher grid (undefined in other zones). */
  size?: WidgetSize;
}

export interface WidgetSize {
  /** Columns and rows (any size; a stored width larger than the current column count is already shrunk to fit). */
  w: number;
  h: number;
  /**
   * The box in DESIGN pixels: the coordinate space your CSS px live in. The runtime lays the widget out in this space and scales it to the
   * real cell (`scale`), so 1 CSS px is always proportional to the grid: a 1x1 widget is 122 design px on any grid configuration
   * (ScreenDoc.launcherGrid), a 2x2 is 268 (with the default 24 px gap). Fractional when the gap is not the default.
   */
  width: number;
  height: number;
  /** How much the runtime scales the design space to real screen pixels (1 on the default grid; < 1 on denser grids). */
  scale: number;
  /** Shape class of the box: 'dot' (small both ways, icon only), 'bar' (short and wide), 'card' (everything taller). Decided in design px. */
  shape: 'dot' | 'bar' | 'card';
}

export const RuntimeContext = createContext<RuntimeApi>(null as unknown as RuntimeApi);
export const WidgetContext = createContext<WidgetInfo>(null as unknown as WidgetInfo);
